// Controller vibration (Settings › Controls, owner-approved 2026-11-02). App-side only: main.ts maps SimEvents to pulses;
// nothing here feeds back into the sim. Short pulses through the Gamepad API's dual-rumble effect where the browser
// supports it (Chromium on Windows / Android / ChromeOS with XInput / standard pads), the older hapticActuators.pulse()
// as a fallback, otherwise nothing. A gate rate-limits pulses so it never buzzes constantly in a busy fight.
import type { RumbleLevel } from './settingsPrefs';

export type RumbleKind = 'hurt' | 'blast' | 'surge' | 'boss';
export interface Pulse { strong: number; weak: number; ms: number }
/** authored at High; Low scales the magnitudes */
export const PULSES: Record<RumbleKind, Pulse> = {
  hurt: { strong: 0.35, weak: 0.65, ms: 110 },
  blast: { strong: 0.75, weak: 0.45, ms: 160 },
  surge: { strong: 0.6, weak: 0.9, ms: 260 },
  boss: { strong: 1, weak: 0.6, ms: 520 },
};
export const LEVEL_K: Record<RumbleLevel, number> = { off: 0, low: 0.45, high: 1 };
/** minimum seconds between two pulses of the same kind */
const KIND_GAP: Record<RumbleKind, number> = { hurt: 0.4, blast: 0.6, surge: 1.0, boss: 2.0 };
/** any two pulses at least this far apart (s) */
const ANY_GAP = 0.15;
/** at most this many hurt / blast pulses in any WINDOW seconds (surge / boss always get through) */
const BUDGET = 4, WINDOW = 3;

/** Pure rate limiter (time injected, unit-tested). */
export class RumbleGate {
  private lastKind: Record<RumbleKind, number> = { hurt: -1e9, blast: -1e9, surge: -1e9, boss: -1e9 };
  private lastAny = -1e9;
  private recent: number[] = [];
  allow(kind: RumbleKind, now: number): boolean {
    if (now - this.lastKind[kind] < KIND_GAP[kind]) return false;
    const major = kind === 'surge' || kind === 'boss';
    if (!major && now - this.lastAny < ANY_GAP) return false;
    while (this.recent.length && now - this.recent[0] >= WINDOW) this.recent.shift();
    if (!major && this.recent.length >= BUDGET) return false;
    this.lastKind[kind] = now; this.lastAny = now;
    if (!major) this.recent.push(now);
    return true;
  }
}

/** the pulse to play at a level (null when vibration is off) */
export function pulseFor(kind: RumbleKind, level: RumbleLevel): Pulse | null {
  const k = LEVEL_K[level]; if (!k) return null;
  const p = PULSES[kind];
  return { strong: Math.min(1, p.strong * k), weak: Math.min(1, p.weak * k), ms: Math.round(p.ms * (level === 'low' ? 0.85 : 1)) };
}

interface Actuator { playEffect?(type: string, params: Record<string, number>): Promise<unknown>; pulse?(value: number, duration: number): Promise<unknown> }
/** fire one pulse on a pad; every capability is optional and every failure is swallowed */
export function playPulse(pad: Gamepad | null, p: Pulse): boolean {
  if (!pad) return false;
  try {
    const va = (pad as unknown as { vibrationActuator?: Actuator | null }).vibrationActuator;
    if (va && typeof va.playEffect === 'function') {
      void va.playEffect('dual-rumble', { startDelay: 0, duration: p.ms, strongMagnitude: p.strong, weakMagnitude: p.weak }).catch(() => { /* unsupported effect */ });
      return true;
    }
    const ha = (pad as unknown as { hapticActuators?: Actuator[] }).hapticActuators?.[0];
    if (ha && typeof ha.pulse === 'function') { void ha.pulse(Math.max(p.strong, p.weak), p.ms).catch(() => { /* ignore */ }); return true; }
  } catch { /* no haptics */ }
  return false;
}

export class Rumble {
  level: RumbleLevel = 'low';
  private gate = new RumbleGate();
  constructor(private pad: () => Gamepad | null) {}
  /** request a pulse; now in seconds (performance.now() / 1000) */
  fire(kind: RumbleKind, now: number): void {
    const p = pulseFor(kind, this.level); if (!p) return;
    const pad = this.pad(); if (!pad) return;
    if (!this.gate.allow(kind, now)) return;
    playPulse(pad, p);
  }
  /** a short sample pulse when the setting changes (so you feel the strength you picked) */
  preview(): void { const p = pulseFor('hurt', this.level); if (p) playPulse(this.pad(), p); }
}
